import { CommonModule, DatePipe, PercentPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import dayjs from 'dayjs';
import { TooltipModule } from 'primeng/tooltip';

import { PacingData, ProphetMetrics, ViewDatesOutput } from '../../../generated/graphql.generated';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { copyTextToClipboard } from '../../utilities/clipboard.util';

export type ForecastLayout = 'card' | 'strip';

@Component({
  selector: 'app-forecast-card',
  standalone: true,
  imports: [CommonModule, DatePipe, PercentPipe, TooltipModule],
  templateUrl: './forecast-card.component.html',
  styleUrls: ['./forecast-card.component.scss']
})
export class ForecastCardComponent {
  @Input() forecast: ViewDatesOutput['prophet'] | undefined | null;
  @Input() pacing: PacingData | undefined | null;
  @Input() layout: ForecastLayout = 'card';
  @Input() prophetMetrics: ProphetMetrics | null = null;
  /** YYYY-MM-DD string for the date this forecast belongs to. Used to show historical label. */
  @Input() date: string | null = null;

  // Actuals for comparison in tooltip
  @Input() actualJobs: number = 0;
  @Input() actualTrucksUsed: number = 0;
  @Input() truckCapacity: number = 0;
  @Input() actualCrewUsed: number = 0;
  @Input() crewCapacity: number = 0;

  private readonly notify: FreyaNotificationsService;

  constructor(notify: FreyaNotificationsService) {
      this.notify = notify;
  }

  get showContent(): boolean {
    return !!((this.forecast && this.forecast.outcome) || this.pacing);
  }

  get showPacing(): boolean {
      return !!this.pacing;
  }

  get showForecast(): boolean {
      return !!(this.forecast && this.forecast.outcome);
  }

  get trucksAvailable(): number {
      return Math.max(0, this.truckCapacity - this.actualTrucksUsed);
  }

  get crewAvailable(): number {
      return Math.max(0, this.crewCapacity - this.actualCrewUsed);
  }

  /** Prophet's forecast_quality (0..1) for this day, or null when unavailable. */
  get confidence(): number | null {
      return this.forecast?.discount?.forecast_quality ?? null;
  }

  /** True when the forecast date is in the past (before today). */
  get isHistorical(): boolean {
      if (!this.date) { return false; }
      return dayjs(this.date).isBefore(dayjs(), 'day');
  }

  /** The strip box IS the copy control (Ray), mirroring pacing: one click
   *  anywhere on the forecast box writes a Google Sheets compatible TSV of
   *  the forecast to the clipboard. No separate copy icon. */
  get canCopy(): boolean {
      return this.layout === 'strip' && !!this.forecast?.outcome;
  }

  async copyForecastToClipboard(): Promise<void> {
      if (!this.canCopy) { return; }

      const copied = await copyTextToClipboard(this.buildTsv());
      if (copied) {
          this.notify.success('Forecast data copied to clipboard');
      } else {
          this.notify.error('Failed to copy forecast data');
      }
  }

  private buildTsv(): string {
      const o = this.forecast!.outcome;
      const lines: string[] = [];

      lines.push('FORECAST SUMMARY');
      lines.push(['Date', this.date ?? ''].join('\t'));
      lines.push(['Metric', 'Value'].join('\t'));
      lines.push(['Predicted Jobs', String(o.expected_bookings ?? 0)].join('\t'));
      if ((o.expected_trucks ?? 0) > 0) {
          lines.push(['Trucks Predicted', String(o.expected_trucks)].join('\t'));
          lines.push(['Trucks Available', String(this.trucksAvailable)].join('\t'));
      }
      if ((o.expected_crew ?? 0) > 0) {
          lines.push(['Crew Predicted', String(o.expected_crew)].join('\t'));
          lines.push(['Crew Available', String(this.crewAvailable)].join('\t'));
      }
      const tier = this.forecast?.discount?.tier;
      if (tier) {
          lines.push(['Pricing Tier', `${tier.name} (${tier.percent > 0 ? '+' : ''}${tier.percent}%)`].join('\t'));
      }
      return lines.join('\n');
  }
}
